/**
 * 결제 목록 테이블 컴포넌트
 * - 이용권 구매/취소/회수 내역 표시
 */

import type { PaymentItem, PaymentStatus, PaymentType } from '../../types/payment';

interface PaymentListProps {
  payments: PaymentItem[];
  /** 결제유형 컬럼 숨김 — 온라인/오프라인 분리 페이지처럼 유형이 고정된 화면용 */
  hideTypeColumn?: boolean;
  /** 크레딧 컬럼 숨김 — 온라인 결제 화면(크레딧 차감 개념 없음)용 */
  hideCreditColumn?: boolean;
  /** 상태 라벨 프리셋 — 오프라인은 "결제" 대신 "발급" 문구 사용 (필터와 동일 표기) */
  statusLabels?: Partial<Record<PaymentStatus, string>>;
}

export function PaymentList({
  payments,
  hideTypeColumn = false,
  hideCreditColumn = false,
  statusLabels,
}: PaymentListProps) {
  const columnCount = 11 - (hideTypeColumn ? 1 : 0) - (hideCreditColumn ? 1 : 0);
  const getPaymentTypeLabel = (type: PaymentType): string => {
    return type === 'ONLINE' ? '온라인 결제' : '수동 발급';
  };

  const getPaymentTypeColor = (type: PaymentType): string => {
    return type === 'ONLINE'
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusLabel = (status: PaymentStatus): string => {
    if (status === 'PAID') return statusLabels?.PAID ?? '결제';
    if (status === 'CANCELED') return statusLabels?.CANCELED ?? '취소';
    return statusLabels?.REVOKE ?? '회수';
  };

  const getPaymentStatusColor = (status: PaymentStatus): string => {
    if (status === 'PAID') return 'bg-green-100 text-green-800';
    if (status === 'CANCELED') return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-900';
  };

  const formatDateTime = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 w-[72px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                번호
              </th>
              {!hideTypeColumn && (
                <th className="px-6 py-3 w-[140px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  결제유형
                </th>
              )}
              <th className="px-6 py-3 w-[130px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                결제상태
              </th>
              <th className="px-6 py-3 w-[90px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                회원명
              </th>
              <th className="px-6 py-3 w-[190px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                이메일
              </th>
              <th className="px-6 py-3 w-[160px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                사업장
              </th>
              <th className="px-6 py-3 w-[190px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                이용권
              </th>
              <th className="px-6 py-3 w-[110px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                이용권 횟수
              </th>
              <th className="px-6 py-3 w-[160px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                금액
              </th>
              {!hideCreditColumn && (
                <th className="px-6 py-3 w-[120px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  크레딧
                </th>
              )}
              <th className="px-6 py-3 w-[210px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                일시
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payments.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="px-6 py-12 text-center text-gray-500">
                  이용권 내역이 없습니다.
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.paymentSeq} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 w-[72px] whitespace-nowrap text-sm text-gray-900">
                    {payment.paymentSeq}
                  </td>
                  {!hideTypeColumn && (
                    <td className="px-6 py-4 w-[140px] whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentTypeColor(
                          payment.paymentType
                        )}`}
                      >
                        {getPaymentTypeLabel(payment.paymentType)}
                      </span>
                    </td>
                  )}
                  <td className="px-6 py-4 w-[130px] whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(
                        payment.paymentStatus
                      )}`}
                    >
                      {getPaymentStatusLabel(payment.paymentStatus)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div
                      className="max-w-[90px] truncate"
                      title={payment.userName}
                    >
                      {payment.userName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div
                      className="max-w-[190px] truncate"
                      title={payment.userEmail}
                    >
                      {payment.userEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div
                      className="max-w-[160px] truncate"
                      title={payment.companyName || '-'}
                    >
                      {payment.companyName || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div
                      className="max-w-[190px] truncate"
                      title={payment.ticketName}
                    >
                      {payment.ticketName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap w-[110px] text-sm text-gray-500">
                    {payment.splitCount}회
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap w-[160px] text-sm text-right font-medium text-gray-900">
                    {payment.amount.toLocaleString()}원
                  </td>
                  {!hideCreditColumn && (
                    <td className="px-6 py-4 whitespace-nowrap w-[120px] text-sm text-right">
                      {payment.paymentType === 'OFFLINE' ? (
                        payment.creditsDeducted != null && payment.creditsDeducted > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 tabular-nums">
                            -{payment.creditsDeducted.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(payment.dtRegist)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



